// SPDX-License-Identifier: AGPL-3.0-or-later

import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import {makeAutoObservable} from 'mobx';

/** The new agent chat's prompt (the page and the Send to an agent panel share it). */
export const NEW_CHAT_DRAFT = 'new';

export const threadDraftKey = (machineId: string, threadId: string) => `${machineId}/${threadId}`;

/**
 * Unsent agent prompts, kept across leaving the thread and reloads: one per thread, and one for a
 * new chat. Local to this device, like the Konvo and WhatsApp chat drafts.
 */
class AgentDrafts {
	drafts: Record<string, string> = {};
	/** How many pictures and files a draft holds (the files themselves are in AgentDraftFiles). */
	files: Record<string, number> = {};
	/**
	 * The project the new chat is on, per machine: picked by hand, opened with "New chat here", or
	 * last started in. Leaving the page and coming back (or a reload) keeps it with the prompt.
	 */
	projects: Record<string, string> = {};

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void makePersistent(this, 'AgentDrafts', ['drafts', 'files', 'projects'], {writeDelayMs: 400});
	}

	/** The project a new chat on this machine opens on; null before one was picked or started in. */
	newChatProject(machineId: string): string | null {
		return this.projects[machineId] ?? null;
	}

	setNewChatProject(machineId: string, projectId: string): void {
		if (this.projects[machineId] !== projectId) this.projects[machineId] = projectId;
	}

	fileCount(key: string): number {
		return this.files[key] ?? 0;
	}

	setFileCount(key: string, count: number): void {
		if (count > 0) this.files[key] = count;
		else if (key in this.files) delete this.files[key];
	}

	get(key: string): string {
		return this.drafts[key] ?? '';
	}

	/** The composer's text exactly as typed; empty clears it. */
	set(key: string, text: string): void {
		if (!text) this.clear(key);
		else if (this.drafts[key] !== text) this.drafts[key] = text;
	}

	/** One line for a list row; whitespace alone is no draft. */
	preview(key: string): string {
		return this.get(key).replace(/\s+/g, ' ').trim();
	}

	clear(key: string): void {
		if (key in this.drafts) delete this.drafts[key];
	}
}

export default new AgentDrafts();
